import type { OidcClient } from '@axa-fr/react-oidc';
import { SESSION_EXPIRED_PATH } from '@healthhub/message-channel';
import { clearAllUserSession, MessageChannelBridge } from '@healthhub/react-ui';

import { sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import { persistor } from '@shared/stores';
import { MESSAGE_ACTION } from '@shared/utils/bridge';
import { clearCookies } from '@shared/utils/cookie';

import { SECURE_ACCESS_REDIRECTION_URL_CODE } from '../../configs/secureAccessRedirection';

interface IHandleAuthErrorProps {
  errorUrl?: string;
  isForbidden: boolean;
  isMobileView: boolean;
  isPortalView: boolean;
  isUnauthorised: boolean;
  oidc: OidcClient;
}

// TO REMOVE AFTER NO CHANNEL APP SUPPORT, SHOULD NOT USE THIS ANYMORE
const UNAUTHORISED_PATH = '?action=unauthorised';

const getNavigationPath = ({
  errorUrl,
  isForbidden,
  isMobileView,
  isPortalView,
  isUnauthorised,
}: Omit<IHandleAuthErrorProps, 'oidc'>): string | undefined => {
  // Unauthorized in portal/mobile -> session expired page
  if (isUnauthorised && (isMobileView || isPortalView)) {
    return SESSION_EXPIRED_PATH;
  }

  // Unauthorized in standard view -> unauthorized
  if ((isUnauthorised || isForbidden) && !isMobileView && !isPortalView) {
    return UNAUTHORISED_PATH;
  }

  // Forbidden in non-portal -> check secure access or unauthorized
  if (isForbidden && isMobileView) {
    const matchedURL = errorUrl && Object.keys(SECURE_ACCESS_REDIRECTION_URL_CODE).find((url) => url.includes(errorUrl));

    if (matchedURL && SECURE_ACCESS_REDIRECTION_URL_CODE[matchedURL]) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      MessageChannelBridge.sendMessageToNativeBridge(
        MESSAGE_ACTION.navigation({
          code: SECURE_ACCESS_REDIRECTION_URL_CODE[matchedURL],
          attributes: {
            launchMode: 'REPLACE_INSTANCE',
            params: {
              path: returnUrl,
            },
          },
        })
      );
      return '';
    }
    return UNAUTHORISED_PATH;
  }

  return undefined;
};

export const handleAuthError = async ({
  errorUrl,
  isForbidden,
  isMobileView,
  isPortalView,
  isUnauthorised,
  oidc,
}: IHandleAuthErrorProps) => {
  clearAllUserSession({ persistor, clearCookies });
  const logoutUrl =
    isForbidden && isPortalView
      ? sitecoreHomeBaseUrl()
      : getNavigationPath({ errorUrl, isForbidden, isMobileView, isPortalView, isUnauthorised });
  await oidc.logoutAsync(logoutUrl);
};
